import React, { useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import { useTranslation } from "react-i18next";

type FAQItem = {
  id: string;
  question: string;
  answer: string;
};

type FAQCategory = {
  key: string;
  label: string;
  items: FAQItem[];
};

const faqData: FAQCategory[] = [
  {
    key: "booking",
    label: "Booking & Packages",
    items: [
      {
        id: "booking-1",
        question:
          "How far in advance should I book my holiday package?",
        answer:
          "We recommend booking 2-3 months in advance for best pricing and availability.",
      },
      {
        id: "booking-2",
        question:
          "Can I customize a pre-designed holiday package?",
        answer:
          "Yes, all packages can be tailored to your preferences.",
      },
      {
        id: "booking-3",
        question:
          "What payment methods do you accept?",
        answer:
          "We accept cards, bank transfers, and flexible payment plans.",
      },
    ],
  },
  {
    key: "visas",
    label: "Visa Services",
    items: [
      {
        id: "visa-1",
        question:
          "Do I need to visit the embassy for a Schengen visa?",
        answer:
          "Yes, if biometrics are not submitted in last 59 months.",
      },
      {
        id: "visa-2",
        question: "What happens if my visa is rejected?",
        answer:
          "We provide guidance for re-application and flexible options.",
      },
    ],
  },
  {
    key: "cancellations",
    label: "Cancellations & Refunds",
    items: [
      {
        id: "cancel-1",
        question: "What is your cancellation policy?",
        answer:
          "Full refund before 30 days. Partial after that depending on timeline.",
      },
    ],
  },
];

const FAQSection: React.FC = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<string>("booking");
  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);

  const currentCategory = faqData.find(
    (cat) => cat.key === activeTab
  );

  return (
    <section className="py-12 md:py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Heading */}
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-brand-dark mb-4">
            {t("Frequently Asked Questions")}
          </h2>
          <p className="text-gray-600 text-sm md:text-base">
            {t("Find quick answers to common questions.")}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {faqData.map((tab) => (
            <button
              key={tab.key}
              onClick={() => {
                setActiveTab(tab.key);
                setActiveAccordion(null);
              }}
              className={`px-4 md:px-6 py-2 rounded-full text-xs md:text-sm font-medium transition ${
                activeTab === tab.key
                  ? "bg-brand-dark text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {t(tab.label)}
            </button>
          ))}
        </div>

        {/* Accordion */}
        <div className="space-y-4">
          {currentCategory?.items.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <button
                onClick={() =>
                  setActiveAccordion(
                    activeAccordion === item.id ? null : item.id
                  )
                }
                className="w-full px-6 py-5 flex justify-between items-center"
              >
                <span className="font-semibold text-brand-dark pr-4">
                  {t(item.question)}
                </span>

                <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center">
                  <FaChevronDown
                    className={`transition-transform ${
                      activeAccordion === item.id
                        ? "rotate-180"
                        : ""
                    }`}
                  />
                </div>
              </button>

              {activeAccordion === item.id && (
                <div className="px-6 pb-5 text-gray-600 text-sm leading-relaxed">
                  {t(item.answer)}
                </div>
              )}
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default FAQSection;
